/**
 * Light mutations of an existing chart — same spirit as Motif / Wave:
 * one coherent, whole-grid transform that keeps the repeat readable.
 * No per-bead noise, no stacked geometric scars.
 */

import { normalizeHex } from "./harmonicPalette";
import { isEmptyBead } from "./stringingOrder";

function randInt(rng, min, max) {
  return min + Math.floor(rng() * (max - min + 1));
}

function pick(rng, list) {
  return list[randInt(rng, 0, list.length - 1)];
}

function cellKey(cell) {
  return normalizeHex(cell) || String(cell ?? "").trim().toLowerCase();
}

/** Pad/trim a flat pattern to cols * rows. */
export function normalizePatternGrid(pattern, cols, rows, defaultColor) {
  const width = Math.max(1, Math.floor(Number(cols)) || 1);
  const height = Math.max(1, Math.floor(Number(rows)) || 1);
  const total = width * height;
  const src = Array.isArray(pattern) ? pattern : [];
  const fill = defaultColor || "#e6e6e6";
  const out = Array.from({ length: total }, (_, i) => {
    const value = src[i];
    return value == null || value === "" ? fill : value;
  });
  return { grid: out, cols: width, rows: height };
}

/** Unique non-empty colors already on the chart, in first-seen order. */
export function chartColors(grid, defaultColor) {
  const seen = [];
  const keys = new Set();
  for (const cell of grid) {
    if (isEmptyBead(cell, defaultColor)) continue;
    const key = cellKey(cell);
    if (!key || keys.has(key)) continue;
    keys.add(key);
    seen.push(normalizeHex(cell) || cell);
  }
  return seen;
}

export function patternHasContent(pattern, defaultColor) {
  if (!Array.isArray(pattern) || pattern.length === 0) return false;
  return pattern.some((cell) => !isEmptyBead(cell, defaultColor));
}

function toRows(grid, cols, rows) {
  const out = [];
  for (let r = 0; r < rows; r += 1) {
    out.push(grid.slice(r * cols, r * cols + cols));
  }
  return out;
}

function fromRows(rowGrid) {
  return rowGrid.flat();
}

function rotate(arr, by) {
  const n = arr.length;
  if (n === 0) return arr.slice();
  const k = ((by % n) + n) % n;
  if (k === 0) return arr.slice();
  return arr.slice(k).concat(arr.slice(0, k));
}

/** Smallest row-block period (motif height), or full height. */
export function detectRowPeriod(rowGrid) {
  const h = rowGrid.length;
  if (h === 0) return 0;
  const sigs = rowGrid.map((row) => row.map(cellKey).join("|"));
  for (let p = 1; p <= h; p += 1) {
    let ok = true;
    for (let i = 0; i < h; i += 1) {
      if (sigs[i] !== sigs[i % p]) {
        ok = false;
        break;
      }
    }
    if (ok) return p;
  }
  return h;
}

/** Smallest column period inside rows (motif width), or full width. */
export function detectColPeriod(rowGrid) {
  if (rowGrid.length === 0) return 0;
  const w = rowGrid[0].length;
  for (let p = 1; p <= w; p += 1) {
    let ok = true;
    for (let r = 0; r < rowGrid.length && ok; r += 1) {
      const row = rowGrid[r];
      for (let c = 0; c < w; c += 1) {
        if (cellKey(row[c]) !== cellKey(row[c % p])) {
          ok = false;
          break;
        }
      }
    }
    if (ok) return p;
  }
  return w;
}

/**
 * Shift within the motif period (never a full period — that would be a no-op
 * on a clean tile). Falls back to ±1 when there is no shorter repeat.
 */
function phaseAmount(period, full, rng) {
  if (period > 1 && period < full) {
    const step = randInt(rng, 1, period - 1);
    return (rng() < 0.5 ? -1 : 1) * step;
  }
  return pick(rng, [1, -1]);
}

function opRowShift(grid, cols, rows, rng, rowPeriod) {
  const amount = phaseAmount(rowPeriod, rows, rng);
  const rowGrid = toRows(grid, cols, rows);
  return {
    grid: fromRows(rotate(rowGrid, amount)),
    op: { type: "rowShift", amount, period: rowPeriod },
  };
}

function opColShift(grid, cols, rows, rng, colPeriod) {
  const amount = phaseAmount(colPeriod, cols, rng);
  const rowGrid = toRows(grid, cols, rows).map((row) => rotate(row, amount));
  return {
    grid: fromRows(rowGrid),
    op: { type: "colShift", amount, period: colPeriod },
  };
}

function opMirrorH(grid, cols, rows) {
  const rowGrid = toRows(grid, cols, rows).map((row) => row.slice().reverse());
  return { grid: fromRows(rowGrid), op: { type: "mirrorH" } };
}

function opMirrorV(grid, cols, rows) {
  const rowGrid = toRows(grid, cols, rows).slice().reverse();
  return { grid: fromRows(rowGrid), op: { type: "mirrorV" } };
}

function opRotate180(grid, cols, rows) {
  const rowGrid = toRows(grid, cols, rows)
    .slice()
    .reverse()
    .map((row) => row.slice().reverse());
  return { grid: fromRows(rowGrid), op: { type: "rotate180" } };
}

/** Cycle every painted role forward — structure stays, colors walk one step. */
function opCycleColors(grid, defaultColor) {
  const colors = chartColors(grid, defaultColor);
  if (colors.length < 2) {
    return { grid: grid.slice(), op: { type: "cycleColors", skipped: true } };
  }
  const forward = new Map();
  colors.forEach((color, i) => {
    forward.set(cellKey(color), colors[(i + 1) % colors.length]);
  });
  const next = grid.map((cell) => {
    if (isEmptyBead(cell, defaultColor)) return cell;
    return forward.get(cellKey(cell)) || cell;
  });
  return { grid: next, op: { type: "cycleColors", count: colors.length } };
}

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

/**
 * Map a color grid to letters. Empty/default cells stay null so they remain
 * the canvas default on recolor.
 */
export function colorsToLetters(grid, defaultColor) {
  const colorToLetter = new Map();
  const letterToColor = {};
  const letters = grid.map((cell) => {
    if (isEmptyBead(cell, defaultColor)) return null;
    const key = cellKey(cell);
    if (!colorToLetter.has(key)) {
      const letter = ALPHABET[colorToLetter.size] || "Z";
      colorToLetter.set(key, letter);
      letterToColor[letter] = normalizeHex(cell) || cell;
    }
    return colorToLetter.get(key);
  });
  return { letters, letterToColor };
}

export function mapLettersToColors(letters, letterToColor, defaultColor) {
  const fill = defaultColor || "#e6e6e6";
  return letters.map((letter) => {
    if (letter == null || letter === "") return fill;
    return letterToColor[letter] || fill;
  });
}

const MIN_TWEAK_LEVEL = 1;
const MAX_TWEAK_LEVEL = 5;

function clampTweakLevel(level) {
  const n = Math.floor(Number(level));
  if (!Number.isFinite(n)) return MIN_TWEAK_LEVEL;
  return Math.max(MIN_TWEAK_LEVEL, Math.min(MAX_TWEAK_LEVEL, n));
}

/**
 * Tweak an existing pattern with calm whole-grid rules.
 * `level` (1–5) is how many successful changes one click applies.
 * @returns {null | { pattern, letters, letterToColor, meta }}
 */
export function tweakPattern({
  pattern,
  cols,
  rows,
  defaultColor = "#e6e6e6",
  rng = Math.random,
  level = 1,
}) {
  if (!patternHasContent(pattern, defaultColor)) return null;
  const targetOps = clampTweakLevel(level);

  const { grid: start, cols: width, rows: height } = normalizePatternGrid(
    pattern,
    cols,
    rows,
    defaultColor
  );
  const rowGrid = toRows(start, width, height);
  const rowPeriod = detectRowPeriod(rowGrid);
  const colPeriod = detectColPeriod(rowGrid);

  const pool = ["rowShift", "colShift", "mirrorH", "mirrorV", "rotate180"];
  if (chartColors(start, defaultColor).length >= 2) pool.push("cycleColors");

  let grid = start.slice();
  const ops = [];
  const sameGrid = (next, prev) =>
    next.length === prev.length && next.every((cell, i) => cell === prev[i]);

  const run = (name) => {
    if (name === "rowShift") {
      return opRowShift(grid, width, height, rng, rowPeriod);
    }
    if (name === "colShift") {
      return opColShift(grid, width, height, rng, colPeriod);
    }
    if (name === "mirrorH") return opMirrorH(grid, width, height);
    if (name === "mirrorV") return opMirrorV(grid, width, height);
    if (name === "rotate180") return opRotate180(grid, width, height);
    return opCycleColors(grid, defaultColor);
  };

  // Retry no-ops (e.g. rotate180 on a checker) until `level` changes land.
  let attempts = 0;
  const maxAttempts = pool.length * targetOps + pool.length;
  while (ops.length < targetOps && attempts < maxAttempts) {
    attempts += 1;
    const name = pool[randInt(rng, 0, pool.length - 1)];
    const result = run(name);
    if (!result.op.skipped && !sameGrid(result.grid, grid)) {
      grid = result.grid;
      ops.push(result.op);
    }
  }

  if (ops.length === 0) {
    const result = opColShift(grid, width, height, () => 0.1, colPeriod);
    grid = result.grid;
    ops.push(result.op);
  }

  const { letters, letterToColor } = colorsToLetters(grid, defaultColor);
  return {
    pattern: grid,
    letters,
    letterToColor,
    meta: {
      style: "tweak",
      ops,
      rowPeriod,
      colPeriod,
      colorCount: Object.keys(letterToColor).length,
    },
  };
}
